import React, { useEffect, useMemo, useState } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Select,
  Button,
  RadioGroup,
  Radio,
  Stack,
  useToast,
  HStack,
  Input,
  Text,
  Box,
  Checkbox,
  Divider,
  Textarea,
  Code,
  Tag,
} from '@chakra-ui/react';
import {  } from '../utils/predefinedEvents';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import { getTimezones, getUserTimezone } from '../../../utils/timezone';
import ReactSelect from 'react-select';

export default function StartProperties({ nodeConfig, setNodeConfig, updateNodeData }) {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const toast = useToast();
  const [cohorts, setCohorts] = useState([]);
  const [eventAttributes, setEventAttributes] = useState({});
  const [loadingAttributes, setLoadingAttributes] = useState({});
  const timezoneOptions = useMemo(() => getTimezones(), []);
  const [triggerSampleJson, setTriggerSampleJson] = useState(nodeConfig.triggerSampleJson || '');
  const [triggerGeneratedFields, setTriggerGeneratedFields] = useState(nodeConfig.triggerContractFields || []);

  useEffect(() => {
    setTriggerSampleJson(nodeConfig.triggerSampleJson || '');
  }, [nodeConfig.triggerSampleJson]);

  useEffect(() => {
    setTriggerGeneratedFields(nodeConfig.triggerContractFields || []);
  }, [nodeConfig.triggerContractFields]);

  const generateTriggerFields = () => {
    try {
      const parsed = JSON.parse(triggerSampleJson);
      const fields = [];
      const extractFields = (obj, prefix = '') => {
        Object.keys(obj).forEach((key) => {
          const value = obj[key];
          const path = prefix ? `${prefix}.${key}` : key;
          let type = typeof value;
          if (Array.isArray(value)) type = 'array';
          else if (value === null) type = 'string';
          else if (typeof value === 'object') type = 'object';

          fields.push({
            fieldName: key,
            jsonPath: path,
            type,
            example: typeof value === 'object' ? JSON.stringify(value) : String(value),
          });

          if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            extractFields(value, path);
          }
        });
      };
      extractFields(parsed);
      setTriggerGeneratedFields(fields);
      setNodeConfig({ ...nodeConfig, triggerContractFields: fields, triggerSampleJson });
    } catch (e) {
      alert('Invalid JSON format');
    }
  };

  // Get user's current timezone as default
  const userTimezone = useMemo(() => getUserTimezone(), []);

  useEffect(() => {
    if (nodeConfig.enableSchedule && !nodeConfig.scheduleTimezone) {
      setNodeConfig(prev => ({
        ...prev,
        scheduleTimezone: userTimezone
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeConfig.enableSchedule]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
      fontSize: '14px',
      borderColor: state.isFocused ? 'var(--brand-primary)' : '#E2E8F0',
      boxShadow: state.isFocused ? '0 0 0 1px var(--brand-primary)' : 'none',
      '&:hover': {
        borderColor: '#CBD5E0'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '32px',
      padding: '0 8px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0'
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '32px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '13px',
      backgroundColor: state.isSelected
        ? 'var(--brand-primary)'
        : state.isFocused
          ? 'var(--brand-tint)'
          : 'white',
      color: state.isSelected ? 'white' : '#2D3748',
      '&:active': {
        backgroundColor: 'var(--brand-primary)'
      }
    })
  };

  useEffect(() => {
    if (
      nodeConfig.triggerType === 'event' &&
      nodeConfig.eventName
    ) {
      fetchEventAttributes(nodeConfig.eventName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeConfig.triggerType, nodeConfig.eventName]);

  useEffect(() => {
    if (
      nodeConfig.eventTriggerMode === 'condition' &&
      (!nodeConfig.eventProperties || nodeConfig.eventProperties.length === 0)
    ) {
      setNodeConfig(prev => ({
        ...prev,
        eventProperties: [
          { attribute: '', operator: 'equals', value: '' }
        ]
      }));
    }

    if (nodeConfig.triggerType !== 'customer_segment') {
      setNodeConfig(prev => ({
        ...prev,
        customerSegmentId: '',
        segmentEntryMode: 'existing',
        segmentExitOnMatch: false,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeConfig.eventTriggerMode]);

  const fetchEventAttributes = async (eventName) => {
    if (!eventName || eventAttributes[eventName]) return;

    setLoadingAttributes(prev => ({ ...prev, [eventName]: true }));

    try {
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`,
        { event: eventName },
        { headers: getApiHeaders() }
      );

      if (response.data.status) {
        setEventAttributes(prev => ({
          ...prev,
          [eventName]: response.data.data.attributes
        }));
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to fetch event attributes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAttributes(prev => ({ ...prev, [eventName]: false }));
    }
  };

  const fetchCohorts = async () => {
    try {
      const res = await API.get(
        `${process.env.REACT_APP_API_URL}/cohorts`,
        { headers: getApiHeaders() }
      );
      if (res.data.status) {
        setCohorts(res.data.data);
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch cohorts',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (nodeConfig.triggerType === 'customer_segment') {
      fetchCohorts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeConfig.triggerType]);

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await API.post(
        `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
        {},
        { headers: getApiHeaders() }
      );

      if (response.data.status && response.data.data && response.data.data.events) {
        setEvents(response.data.data.events);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch events",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch events";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingEvents(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel fontSize="sm">Trigger Type</FormLabel>
        <Select
          size="sm"
          value={nodeConfig.triggerType}
          onChange={(e) => setNodeConfig({ ...nodeConfig, triggerType: e.target.value })}
        >
          <option value="api">API Based Entry</option>
          <option value="event">Event Trigger</option>
          <option value="customer_segment">Customer Segment</option>
        </Select>
      </FormControl>

      {/* API Based Entry Configuration */}
      {nodeConfig.triggerType === 'api' && (
        <VStack spacing={3} align="stretch">
          <Box bg="blue.50" p={3} borderRadius="md" border="1px" borderColor="blue.200">
            <Text fontSize="xs" fontWeight="600" color="blue.900" mb={2}>
              Webhook Endpoint
            </Text>
            <Box 
              bg="white" 
              p={2} 
              borderRadius="md" 
              border="1px" 
              borderColor="blue.300"
              fontFamily="mono"
              fontSize="xs"
              wordBreak="break-all"
            >
              https://api.xnotify.com/v1/flows/{'{flow_id}'}/trigger
            </Box>
            <Text fontSize="xs" color="blue.700" mt={2}>
              Send a POST request to this endpoint to trigger the flow
            </Text>
          </Box>

          <FormControl>
            <FormLabel fontSize="sm">Authentication Method</FormLabel>
            <Select
              size="sm"
              value={nodeConfig.apiAuthMethod || 'bearer'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, apiAuthMethod: e.target.value })}
            >
              {/* <option value="bearer">Bearer Token</option> */}
              <option value="apikey">API Key</option>
              {/* <option value="basic">Basic Auth</option> */}
            </Select>
          </FormControl>

          <Box bg="green.50" p={3} borderRadius="md" border="1px" borderColor="green.200">
            <Text fontSize="xs" fontWeight="600" color="green.900" mb={2}>
              Example Request:
            </Text>
            <Code 
              display="block" 
              whiteSpace="pre" 
              fontSize="10px" 
              p={2} 
              bg="white"
              borderRadius="md"
              overflowX="auto"
            >
{`POST /v1/flows/{flow_id}/trigger
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{
  "user_id": "12345",
  "email": "user@example.com",
  "custom_data": {
    "cart_value": 150
  }
}`}
            </Code>
          </Box>

          <Box bg="orange.50" p={3} borderRadius="md" border="1px" borderColor="orange.200">
            <Text fontSize="xs" fontWeight="600" color="orange.900" mb={1}>
              Template Variables (Optional)
            </Text>
            <Text fontSize="xs" color="orange.800" mb={2}>
              Template variables are only required if your flow includes a Message Node with a template-based message. If you're only using delays, conditions, or free-form messages, you don't need to send template variables in the API request.
            </Text>
            <Text fontSize="xs" color="orange.800" mb={2}>
              When using templates, include the required variables in your request payload:
            </Text>
            <Code 
              display="block" 
              whiteSpace="pre" 
              fontSize="10px" 
              p={2} 
              bg="orange.100"
              borderRadius="md"
              overflowX="auto"
            >
{`{
  "user_id": "12345",
  "template_variables": {
    "customer_name": "John Doe",
    "order_id": "ORD-789"
  }
}`}
            </Code>
          </Box>
        </VStack>
      )}

      {/* Event Trigger Configuration */}
      {nodeConfig.triggerType === 'event' && (
        <>
          <FormControl>
            <FormLabel fontSize="sm">Event Name</FormLabel>
            <Select
              size="sm"
              value={nodeConfig.eventName}
              onChange={(e) => { 
                setNodeConfig({ ...nodeConfig, eventName: e.target.value }); 
                fetchEventAttributes(e.target.value); 
              }}
              placeholder="Select trigger event"
            >
              {events.map((event) => (
                <option key={event.name} value={event.name}>
                  {event.display_name}
                </option>
              ))}
            </Select>
          </FormControl>

          {nodeConfig.eventName && (
            <FormControl>
              <FormLabel fontSize="sm">Trigger Mode</FormLabel>
              <RadioGroup
                value={nodeConfig.eventTriggerMode}
                onChange={(value) => setNodeConfig({ ...nodeConfig, eventTriggerMode: value })}
              >
                <Stack spacing={2}>
                  <Radio value="new_event" size="sm">
                    New event captured
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          )}
        </>
      )}

      {nodeConfig.triggerType === 'event' &&
        nodeConfig.eventTriggerMode === 'condition' && (
          <>
            <FormLabel fontSize="sm">Conditions</FormLabel>
            {nodeConfig?.eventProperties?.map((condition, index) => (
              <VStack key={index} direction="row" spacing={2} align={"start"} className='box-style-card'>
                <FormLabel fontSize="sm">Property</FormLabel>
                <Select
                  size="sm"
                  placeholder="Property"
                  value={condition.attribute}
                  onChange={(e) => {
                    const updated = [...nodeConfig.eventProperties];
                    updated[index].attribute = e.target.value;
                    setNodeConfig({ ...nodeConfig, eventProperties: updated });
                  }}
                >
                  {(eventAttributes[nodeConfig.eventName] || []).map(attr => (
                    <option key={attr.name} value={attr.name}>
                      {attr.name}
                    </option>
                  ))}
                </Select>
                <FormLabel fontSize="sm">Operator</FormLabel>
                <Select
                  size="sm"
                  value={condition.operator}
                  onChange={(e) => {
                    const updated = [...nodeConfig.eventProperties];
                    updated[index].operator = e.target.value;
                    setNodeConfig({ ...nodeConfig, eventProperties: updated });
                  }}
                >
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                </Select>
                <FormLabel fontSize="sm">Value</FormLabel>
                <Input
                  size="sm"
                  placeholder="Value"
                  value={condition.value}
                  onChange={(e) => {
                    const updated = [...nodeConfig.eventProperties];
                    updated[index].value = e.target.value;
                    setNodeConfig({ ...nodeConfig, eventProperties: updated });
                  }}
                />
              </VStack>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setNodeConfig(prev => ({
                  ...prev,
                  eventProperties: [
                    ...prev.eventProperties,
                    { attribute: '', operator: 'equals', value: '' }
                  ]
                }))
              }
            >
              + Add Condition
            </Button>
          </>
        )}

      {/* Customer Segment Configuration */}
      {nodeConfig.triggerType === 'customer_segment' && (
        <>
          <FormControl>
            <FormLabel fontSize="sm">Customer Segment</FormLabel>
            <Select
              size="sm"
              placeholder="Select cohort"
              value={nodeConfig.customerSegmentId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedCohort = cohorts?.cohorts?.find(c => c._id === selectedId) || null;
                setNodeConfig({
                  ...nodeConfig,
                  customerSegmentId: e.target.value,
                  customerCount: selectedCohort ? selectedCohort.user_count : 0,
                  allCustomers: false,
                  segmentName: selectedCohort ? selectedCohort.name : '',
                });
              }}
            >
              {cohorts?.cohorts?.map(cohort => (
                <option key={cohort._id} value={cohort._id}>
                  {cohort.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormLabel fontSize="sm">Segment Behavior</FormLabel>

          <VStack spacing={3} align="stretch">
            {/* Card 1 - Existing Users */}
            <VStack
              w="100%"
              align="start"
              borderWidth="1px"
              borderRadius="md"
              p={4}
              spacing={1}
              cursor="pointer"
              bg={nodeConfig.segmentEntryMode === 'existing' ? 'purple.50' : 'white'}
              borderColor={nodeConfig.segmentEntryMode === 'existing' ? 'purple.400' : 'gray.200'}
              onClick={() =>
                setNodeConfig({ ...nodeConfig, segmentEntryMode: 'existing' })
              }
            >
              <HStack w="100%">
                <input
                  type="radio"
                  checked={nodeConfig.segmentEntryMode === 'existing'}
                  readOnly
                />
                <FormLabel fontSize="sm" fontWeight="600" m={0}>
                  Existing Users
                </FormLabel>
              </HStack>
              <FormLabel fontSize="xs" color="gray.500" m={0}>
                Start campaign immediately for all users already in this segment.
              </FormLabel>
            </VStack>

            {/* Card 2 - Newly Entering Users */}
            <VStack
              w="100%"
              align="start"
              borderWidth="1px"
              borderRadius="md"
              p={4}
              spacing={1}
              cursor="pointer"
              bg={nodeConfig.segmentEntryMode === 'new' ? 'purple.50' : 'white'}
              borderColor={nodeConfig.segmentEntryMode === 'new' ? 'purple.400' : 'gray.200'}
              onClick={() =>
                setNodeConfig({ ...nodeConfig, segmentEntryMode: 'new', allCustomers: true })
              }
            >
              <HStack w="100%">
                <input
                  type="radio"
                  checked={nodeConfig.segmentEntryMode === 'new'}
                  readOnly
                />
                <FormLabel fontSize="sm" fontWeight="600" m={0}>
                  Newly Entering Users
                </FormLabel>
              </HStack>
              <FormLabel fontSize="xs" color="gray.500" m={0}>
                Start campaign only when a user enters this segment in the future.
              </FormLabel>
            </VStack>

            {/* Card 3 - API Based Entry */}
            {/* <VStack
              w="100%"
              align="start"
              borderWidth="1px"
              borderRadius="md"
              p={4}
              spacing={1}
              cursor="pointer"
              bg={nodeConfig.segmentEntryMode === 'api' ? 'purple.50' : 'white'}
              borderColor={nodeConfig.segmentEntryMode === 'api' ? 'purple.400' : 'gray.200'}
              onClick={() =>
                setNodeConfig({ ...nodeConfig, segmentEntryMode: 'api' })
              }
            >
              <HStack w="100%">
                <input
                  type="radio"
                  checked={nodeConfig.segmentEntryMode === 'api'}
                  readOnly
                />
                <FormLabel fontSize="sm" fontWeight="600" m={0}>
                  API Based Entry
                </FormLabel>
              </HStack>
              <FormLabel fontSize="xs" color="gray.500" m={0}>
                Trigger the flow via API for customers in this segment using a webhook endpoint.
              </FormLabel>
            </VStack> */}

            <Box
              borderWidth="1px"
              borderRadius="lg"
              p={3}
              bg={
                nodeConfig.segmentEntryMode === 'existing' ? 'blue.50' : 
                nodeConfig.segmentEntryMode === 'new' ? 'green.50' : 
                'orange.50'
              }
              borderColor={
                nodeConfig.segmentEntryMode === 'existing' ? 'blue.200' : 
                nodeConfig.segmentEntryMode === 'new' ? 'green.200' : 
                'orange.200'
              }
            >
              <Text
                fontSize="xs"
                color={
                  nodeConfig.segmentEntryMode === 'existing' ? 'blue.900' : 
                  nodeConfig.segmentEntryMode === 'new' ? 'green.900' : 
                  'orange.900'
                }
              >
                {nodeConfig.segmentEntryMode === 'existing' ? (
                  <>
                    <Text as="span" fontWeight="bold">
                      One-time Campaign:
                    </Text>{' '}
                    This is a batch campaign that will send to all{' '}
                    {nodeConfig.customerCount?.toLocaleString() || 0} users in the segment
                    when you activate the flow.
                  </>
                ) : nodeConfig.segmentEntryMode === 'new' ? (
                  <>
                    <Text as="span" fontWeight="bold">
                      Automated Trigger:
                    </Text>{' '}
                    This flow will run automatically for each new user added to the{' '}
                    <Text as="span" fontWeight="bold">
                      "{nodeConfig.segmentName || 'Selected Segment'}"
                    </Text>{' '}
                    segment in real-time.
                  </>
                ) : (
                  <>
                    <Text as="span" fontWeight="bold">
                      API Trigger:
                    </Text>{' '}
                    The flow will only execute for users who belong to the "{nodeConfig.segmentName}" segment. 
                    Users not in this segment will be excluded automatically.
                  </>
                )}
              </Text>
            </Box>
          </VStack>

          {/* API Configuration for Segment */}
          {nodeConfig.segmentEntryMode === 'api' && (
            <VStack spacing={3} align="stretch" mt={3}>
              <Box bg="blue.50" p={3} borderRadius="md" border="1px" borderColor="blue.200">
                <Text fontSize="xs" fontWeight="600" color="blue.900" mb={2}>
                  Webhook Endpoint
                </Text>
                <Box 
                  bg="white" 
                  p={2} 
                  borderRadius="md" 
                  border="1px" 
                  borderColor="blue.300"
                  fontFamily="mono"
                  fontSize="xs"
                  wordBreak="break-all"
                >
                  https://api.xnotify.com/v1/flows/{'{flow_id}'}/trigger
                </Box>
                <Text fontSize="xs" color="blue.700" mt={2}>
                  Send a POST request to this endpoint to trigger the flow for users in the selected segment
                </Text>
              </Box>
            </VStack>
          )}

          {/* Schedule Campaign Option - Only for existing users mode */}
          {nodeConfig.triggerType === 'customer_segment' &&
            nodeConfig.customerSegmentId && (
              <>
                <Box borderTop="1px" borderColor="gray.200" my={3} />

                <FormControl display="flex" alignItems="center">
                  <Checkbox
                    id="enableSchedule"
                    isChecked={nodeConfig.enableSchedule}
                    onChange={(e) => setNodeConfig({
                      ...nodeConfig,
                      enableSchedule: e.target.checked,
                      scheduleType: e.target.checked ? 'daily' : undefined,
                      scheduleTime: e.target.checked ? '09:00' : undefined,
                      scheduleTimezone: e.target.checked ? userTimezone : undefined
                    })}
                  />
                  <FormLabel htmlFor="enableSchedule" mb="0" ml={2} cursor="pointer" fontWeight="600">
                    Schedule this campaign
                  </FormLabel>
                </FormControl>

                {nodeConfig.enableSchedule && (
                  <VStack spacing={3} align="stretch" p={3} bg="purple.50" borderRadius="md" border="1px" borderColor="purple.200">
                    <FormControl>
                      <FormLabel fontSize="sm">Schedule Type</FormLabel>
                      <RadioGroup
                        value={nodeConfig.scheduleType || 'daily'}
                        onChange={(value) => setNodeConfig({ ...nodeConfig, scheduleType: value })}
                      >
                        <Stack spacing={2}>
                          <Radio value="daily" size="sm">Daily</Radio>
                          <Radio value="weekly" size="sm">Weekly</Radio>
                          <Radio value="monthly" size="sm">Monthly</Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>

                    {/* Daily Schedule */}
                    {nodeConfig.scheduleType === 'daily' && (
                      <VStack spacing={3} p={3} bg="white" borderRadius="md" border="1px" borderColor="purple.200">
                        <FormControl>
                          <FormLabel fontSize="sm">Time</FormLabel>
                          <Input
                            type="time"
                            size="sm"
                            value={nodeConfig.scheduleTime || '09:00'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, scheduleTime: e.target.value })}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Timezone</FormLabel>
                          <ReactSelect
                            value={timezoneOptions.find(tz => tz.value === nodeConfig.scheduleTimezone)}
                            onChange={(selected) => setNodeConfig({
                              ...nodeConfig,
                              scheduleTimezone: selected?.value || userTimezone
                            })}
                            options={timezoneOptions}
                            styles={customSelectStyles}
                            placeholder="Search timezone..."
                            isSearchable={true}
                            maxMenuHeight={200}
                          />
                        </FormControl>

                        <Text fontSize="xs" color="gray.600">
                          Campaign will run daily at {nodeConfig.scheduleTime || '09:00'} in {nodeConfig.scheduleTimezone || userTimezone}
                        </Text>
                      </VStack>
                    )}

                    {/* Weekly Schedule */}
                    {nodeConfig.scheduleType === 'weekly' && (
                      <VStack spacing={3} p={3} bg="white" borderRadius="md" border="1px" borderColor="purple.200">
                        <FormControl>
                          <FormLabel fontSize="sm">Day of the Week</FormLabel>
                          <Select
                            size="sm"
                            value={nodeConfig.scheduleDayOfWeek || 'monday'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, scheduleDayOfWeek: e.target.value })}
                          >
                            <option value="monday">Monday</option>
                            <option value="tuesday">Tuesday</option>
                            <option value="wednesday">Wednesday</option>
                            <option value="thursday">Thursday</option>
                            <option value="friday">Friday</option>
                            <option value="saturday">Saturday</option>
                            <option value="sunday">Sunday</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Time</FormLabel>
                          <Input
                            type="time"
                            size="sm"
                            value={nodeConfig.scheduleTime || '09:00'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, scheduleTime: e.target.value })}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Timezone</FormLabel>
                          <ReactSelect
                            value={timezoneOptions.find(tz => tz.value === nodeConfig.scheduleTimezone)}
                            onChange={(selected) => setNodeConfig({
                              ...nodeConfig,
                              scheduleTimezone: selected?.value || userTimezone
                            })}
                            options={timezoneOptions}
                            styles={customSelectStyles}
                            placeholder="Search timezone..."
                            isSearchable={true}
                            maxMenuHeight={200}
                          />
                        </FormControl>

                        <Text fontSize="xs" color="gray.600">
                          Campaign will run every {nodeConfig.scheduleDayOfWeek || 'Monday'} at {nodeConfig.scheduleTime || '09:00'} in {nodeConfig.scheduleTimezone || userTimezone}
                        </Text>
                      </VStack>
                    )}

                    {/* Monthly Schedule */}
                    {nodeConfig.scheduleType === 'monthly' && (
                      <VStack spacing={3} p={3} bg="white" borderRadius="md" border="1px" borderColor="purple.200">
                        <FormControl>
                          <FormLabel fontSize="sm">Day of the Month</FormLabel>
                          <Select
                            size="sm"
                            value={nodeConfig.scheduleDayOfMonth || '1'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, scheduleDayOfMonth: e.target.value })}
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={String(day)}>{day}</option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Time</FormLabel>
                          <Input
                            type="time"
                            size="sm"
                            value={nodeConfig.scheduleTime || '09:00'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, scheduleTime: e.target.value })}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Timezone</FormLabel>
                          <ReactSelect
                            value={timezoneOptions.find(tz => tz.value === nodeConfig.scheduleTimezone)}
                            onChange={(selected) => setNodeConfig({
                              ...nodeConfig,
                              scheduleTimezone: selected?.value || userTimezone
                            })}
                            options={timezoneOptions}
                            styles={customSelectStyles}
                            placeholder="Search timezone..."
                            isSearchable={true}
                            maxMenuHeight={200}
                          />
                        </FormControl>

                        <Text fontSize="xs" color="gray.600">
                          Campaign will run on the {nodeConfig.scheduleDayOfMonth || '1'}
                          {nodeConfig.scheduleDayOfMonth === '1' ? 'st' :
                            nodeConfig.scheduleDayOfMonth === '2' ? 'nd' :
                              nodeConfig.scheduleDayOfMonth === '3' ? 'rd' : 'th'} of every month at {nodeConfig.scheduleTime || '09:00'} in {nodeConfig.scheduleTimezone || userTimezone}
                        </Text>
                      </VStack>
                    )}
                  </VStack>
                )}
              </>
            )}
        </>
      )}

      {/* ── Trigger Payload Sample ───────────────────────────────────────────── */}
      <Divider />
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" fontWeight="semibold">Trigger Payload Sample</Text>
          {triggerGeneratedFields.length > 0 ? (
            <Tag colorScheme="green" fontSize="10px">Defined</Tag>
          ) : (
            <Tag colorScheme="gray" fontSize="10px">Not Set</Tag>
          )}
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Define the expected trigger payload structure so variables can be used in flow nodes.
        </Text>

        <FormControl mb={3}>
          <FormLabel fontSize="sm">Paste Sample Trigger Payload (JSON)</FormLabel>
          <Textarea
            value={triggerSampleJson}
            onChange={(e) => {
              setTriggerSampleJson(e.target.value);
              setNodeConfig({ ...nodeConfig, triggerSampleJson: e.target.value });
            }}
            placeholder={'{\n  "first": "John",\n  "order_id": "ORD-123",\n  "amount": 150\n}'}
            fontFamily="mono"
            fontSize="xs"
            rows={6}
            size="sm"
          />
        </FormControl>

        <Button size="sm" colorScheme="blue" onClick={generateTriggerFields} mb={3}>
          ✨ Generate Variables
        </Button>

        {triggerGeneratedFields.length > 0 && (
          <Box border="1px" borderColor="gray.200" borderRadius="md" p={3} maxH="200px" overflowY="auto">
            <Text fontSize="xs" fontWeight="semibold" mb={2}>
              Generated Variables ({triggerGeneratedFields.length})
            </Text>
            <VStack spacing={2} align="stretch">
              {triggerGeneratedFields.map((field, idx) => (
                <Box key={idx} borderBottom="1px" borderColor="gray.100" pb={2}>
                  <HStack justify="space-between" mb={1}>
                    <Code fontSize="xs" color="green.700" wordBreak="break-all">
                      {`{{start.${field.jsonPath}}}`}
                    </Code>
                    <Tag fontSize="9px" variant="outline" flexShrink={0}>{field.type}</Tag>
                  </HStack>
                  <Text fontSize="10px" color="gray.500">
                    Example: <Code fontSize="10px">{field.example}</Code>
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}
      </Box>

      <Button size="sm" colorScheme="green" onClick={updateNodeData}>
        Update Node
      </Button>
    </VStack>
  );
}